/**
 * WidgetContext — [T-DASHBOARD-VISIBLE-HEADER] per-widget React context.
 *
 * Provides per-widget state (mode, title, icon, action handlers, rename state)
 * to ALL descendants of a widget — specifically the `WidgetFrame` →
 * `HeaderSlot` → `WidgetHeaderActions` chain. This decouples the action wiring
 * (configure / duplicate / replace / hide / resize / remove / delete / rename)
 * from the static `FrameProps` interface, which remains backwards-compatible
 * with the 50+ skin consumers.
 *
 * ## Why a context (not prop drilling)?
 *
 * Pre-v4: `WidgetCardChrome` was a single floating overlay that received ALL
 * its state via 16 props from `DashboardBuilder` / `DashboardView`. The
 * v4 redesign splits that overlay into two pieces:
 *
 *   1. `HeaderSlot` — static visible header (icon + title + actions slot)
 *   2. `WidgetHeaderActions` — gear dropdown reading from context
 *
 * The action handlers + rename state are NOT appropriate for `FrameProps`
 * (they are dashboard-level concerns, not per-widget frame concerns). The
 * context lets the dashboard-level provider (built by the consumer agents)
 * share that state with all descendants of the widget without polluting
 * the public `FrameProps` interface.
 *
 * ## Null safety
 *
 * `useWidgetContext` returns `null` when called outside a `WidgetContext.Provider`.
 * This is intentional — it lets `HeaderSlot` and `WidgetHeaderActions` gracefully
 * no-op in unconfigured call sites (Storybook stories, isolated unit tests,
 * widget previews that haven't been wrapped in a provider yet).
 *
 * @see https://react.dev/reference/react/createContext — context API
 * @see https://react.dev/reference/react/useContext — consumer hook
 */
import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

/**
 * Dashboard mode the widget is being rendered in.
 *
 *   - `preview` — user is viewing the dashboard (read-only). No gear button,
 *                 no rename input. Action handlers are empty objects.
 *   - `edit`    — user is editing the dashboard layout. Gear button visible,
 *                 rename input available, action handlers wired.
 */
export type WidgetDashboardMode = 'preview' | 'edit';

/**
 * Action handler bag — all optional. Each handler maps 1:1 to a dropdown
 * menu item in `WidgetHeaderActions`. When a handler is `undefined`, the
 * corresponding menu item is omitted from the dropdown (mirrors the
 * `WidgetCardChrome` precedent where `{onConfigure && (...)}` gates
 * each menu item).
 */
export interface WidgetActionHandlers {
  onConfigure?: () => void;
  onDuplicate?: () => void;
  onReplace?: () => void;
  onHide?: () => void;
  onResizeNarrow?: () => void;
  onResizeWiden?: () => void;
  onRemove?: () => void;
  onDelete?: () => void;
}

/**
 * Rename input state — exposed via context so `HeaderSlot` can swap the
 * title span for an editable `<Input>` without owning the rename state.
 *
 * `null` in preview mode (rename is an edit-mode feature only).
 */
export interface WidgetRenameState {
  isRenaming: boolean;
  value: string;
  onStartRename: () => void;
  onChange: (value: string) => void;
  onCommit: () => void;
  onCancel: () => void;
}

/**
 * The shape consumed by `WidgetHeaderActions` and `HeaderSlot`.
 *
 * `icon` is intentionally optional — `null` when no icon is provided (the
 * header still renders without one, just with no icon wrapper). This is a
 * behavioral change from `WidgetCardChrome`, which always showed a fallback
 * `BarChart3` icon — the v4 design is cleaner when icons are deliberately
 * provided by the widget kind.
 */
export interface WidgetContextValue {
  /** Stable widget id. Echoes `WidgetFrame`'s `id` prop. */
  widgetId: string;
  /** Dashboard mode. Drives the visibility of the gear button. */
  mode: WidgetDashboardMode;
  /** Resolved display title (already prefers `widget.display.title`). */
  title: string;
  /** Optional icon — emoji string or lucide-react node, ~14-16px. */
  icon?: ReactNode;
  /** Action handler bag. Empty object in preview mode. */
  actions: WidgetActionHandlers;
  /** Rename state. `null` in preview mode (no rename). */
  rename: WidgetRenameState | null;
}

export const WidgetContext = createContext<WidgetContextValue | null>(null);
WidgetContext.displayName = 'WidgetContext';

/**
 * Consume the per-widget context.
 *
 * Returns `null` when called outside a `WidgetContext.Provider`. Callers
 * (especially `HeaderSlot`) MUST handle the null case gracefully — this
 * lets `HeaderSlot` work in Storybook stories and isolated unit tests that
 * render it directly without wiring a full dashboard provider.
 */
export const useWidgetContext = (): WidgetContextValue | null =>
  useContext(WidgetContext);
